import { Device, Devices, OperationMode, useAuth } from "@app/shared";
import { useEffect, useState } from "react";
import { Dialog, ErrorAlert } from ".";
import { useDeviceSnapshot } from "../../hooks/useDeviceSnapshot";
import { EnumSelect, Input, InputRow, NumericInput } from "./inputs";

type DeviceProvisionDialogProps = {
    show: boolean;
    /**
     * Closes the wizard. The host refreshes on this: its refresh reloads the facility and
     * FacilityDetails renders a loading screen while that is in flight, which unmounts this
     * dialog — so refreshing between steps would close the wizard mid-run.
     */
    onCancel: () => void;
    /** Called when the user finishes setup and should be taken to the layout designer to align. */
    onContinueToLayout?: (deviceId: string) => void;
    facilityId: string;
    /** Re-run setup on a device that is already here — skips the key step. */
    existingDevice?: Device;
};

/**
 * Steps. Association is step 1 and always happens through its own endpoint, so abandoning the
 * wizard at any later point still leaves a device attached to the location.
 */
type Step = "key" | "offer" | "details" | "purpose" | "photo" | "done";

export const DeviceProvisionDialog: React.FC<DeviceProvisionDialogProps> = (props) => {
    const [step, setStep] = useState<Step>("key");
    // Empty string, not undefined: an undefined value makes the Input uncontrolled until the first
    // keystroke, which React warns about when it flips to controlled.
    const [deviceKey, setDeviceKey] = useState<string>("");
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [device, setDevice] = useState<Device>();
    const [name, setName] = useState<string>("");
    const [heightMeters, setHeightMeters] = useState<number>();
    const [mode, setMode] = useState<OperationMode>();
    const [photoRequested, setPhotoRequested] = useState(false);
    const [photoArrived, setPhotoArrived] = useState<boolean>();
    // A device that has never uploaded still resolves to a photo URL, which then 404s. Showing the
    // browser's broken-image icon reads as a fault; drop the preview instead.
    const [snapshotBroken, setSnapshotBroken] = useState(false);
    const { currentOrganizationId } = useAuth();

    const snapshot = useDeviceSnapshot(step === "photo" ? device : undefined);

    // Reset when the dialog is reopened so a second device doesn't inherit the first one's answers.
    useEffect(() => {
        if (!props.show) return;
        const existing = props.existingDevice;
        setStep(existing ? "details" : "key");
        setDeviceKey("");
        setError(undefined);
        setDevice(existing);
        setName(existing?.name ?? "");
        setHeightMeters(existing?.height ?? undefined);
        setMode(existing?.mode ?? undefined);
        setPhotoRequested(false);
        setPhotoArrived(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show, props.existingDevice?.id]);

    useEffect(() => setSnapshotBroken(false), [snapshot.url]);

    /** Look the device up and attach it to this facility. Nothing past here is required. */
    const associate = async () => {
        setError(undefined);
        setBusy(true);
        try {
            const found = await Devices.getUnassociatedDevice(deviceKey, currentOrganizationId);
            if (!found?.id) {
                // Previously fell through and associated `undefined`.
                setError(`Unable to find device with key ${deviceKey}`);
                return;
            }
            await Devices.associateDevice(found.id, props.facilityId);
            // Re-read: the later steps save the whole Device, and the copy we looked up still has
            // the pre-association facilityId — saving that would move the device straight back out.
            const associated = (await Devices.getById(found.id)) ?? found;
            setDevice(associated);
            setName(associated.name ?? "");
            setHeightMeters(associated.height ?? undefined);
            setMode(associated.mode ?? undefined);
            setStep("offer");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not add the device.");
        } finally {
            setBusy(false);
        }
    };

    /**
     * One save for every answer. Saving per step would enqueue a device command on the first save
     * and the second would then be refused — Save rejects a device with commands still pending.
     */
    const saveAnswers = async () => {
        if (!device) return false;
        setBusy(true);
        try {
            const updated = Object.assign(Object.create(Object.getPrototypeOf(device)), device, { name, height: heightMeters, mode }) as Device;
            const saved = await Devices.save(updated);
            setDevice(saved ?? updated);
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the device.");
            return false;
        } finally {
            setBusy(false);
        }
    };

    const requestPhoto = async () => {
        setPhotoRequested(true);
        setError(undefined);
        try {
            setPhotoArrived(await snapshot.requestNewPhoto());
        } catch (err) {
            // Back to "Request snapshot" so they can retry, or carry on without one.
            setPhotoRequested(false);
            setError(err instanceof Error ? err.message : "Could not request a snapshot.");
        }
    };

    const finish = () => {
        if (device?.id) props.onContinueToLayout?.(device.id);
        props.onCancel();
    };

    const titles: Record<Step, string> = {
        key: "Add Device",
        offer: "Device added",
        details: "Where is it mounted?",
        purpose: "What is this device for?",
        photo: "Camera snapshot",
        done: "Ready to align",
    };

    const body = () => {
        switch (step) {
            case "key":
                return (
                    <>
                        <p>Please enter the device code from the back of the device</p>
                        <ErrorAlert errors={error} />
                        <InputRow>
                            <Input label="Device Key" value={deviceKey} onChange={(e) => setDeviceKey(e.target.value)} />
                        </InputRow>
                    </>
                );

            case "offer":
                return (
                    <>
                        <p className="mb-2">
                            <span className="font-semibold">{device?.name || "The device"}</span> is now attached to this location.
                        </p>
                        <p className="text-sm text-gray-600">A few details now — mounting height and what it is watching — let us calibrate the camera so shapes you draw land in the right place. You can skip and do this later from the device row.</p>
                        <ErrorAlert errors={error} />
                    </>
                );

            case "details":
                return (
                    <>
                        <p className="text-sm text-gray-600 mb-2">Mounting height is the one thing the device cannot measure for itself, and everything the camera projects onto the ground scales from it. Tilt and roll are read from the device, so you do not need to enter them.</p>
                        <ErrorAlert errors={error} />
                        <InputRow>
                            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                        </InputRow>
                        <InputRow>
                            <NumericInput label="Mount height (m)" value={heightMeters} onChange={(v) => setHeightMeters(v)} />
                        </InputRow>
                    </>
                );

            case "purpose":
                return (
                    <>
                        <p className="text-sm text-gray-600 mb-2">This sets the device's operation mode, which decides the radar profiles available to it.</p>
                        <ErrorAlert errors={error} />
                        <InputRow>
                            <EnumSelect enumType={OperationMode} label="Operation Mode" value={mode} onChange={(v) => setMode(v as OperationMode | undefined)} />
                        </InputRow>
                    </>
                );

            case "photo":
                return (
                    <>
                        <p className="text-sm text-gray-600 mb-2">Aligning the camera needs a snapshot from it. The request rides the device's next check-in, so this can take a moment.</p>
                        <ErrorAlert errors={error} />
                        {!snapshot.canRequestNewPhoto && <p className="text-sm text-gray-600">This device's firmware does not support photo requests. You can still align it once a snapshot appears by other means.</p>}
                        {snapshot.isRequesting && <p className="text-sm">Waiting for the device…</p>}
                        {photoArrived === true && <p className="text-sm text-green-700">Snapshot received.</p>}
                        {photoArrived === false && <p className="text-sm text-amber-700">No snapshot yet. The device may be offline — you can carry on and align later.</p>}
                        {snapshot.url && !snapshotBroken ? (
                            <img src={snapshot.url} alt="Latest camera snapshot" onError={() => setSnapshotBroken(true)} className="mt-2 max-h-64 rounded border border-gray-300" />
                        ) : (
                            photoRequested && !snapshot.isRequesting && <div className="mt-2 h-32 flex items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-500">No camera image yet</div>
                        )}
                    </>
                );

            case "done":
                return (
                    <>
                        <p className="mb-2">Next: align the camera so the layout matches what it sees.</p>
                        <p className="text-sm text-gray-600">We will open the layout designer. Use <span className="font-semibold">Align Device → Reference Points</span>, click matching points on the camera and the map, and press Fit. Aim for a mean error under about 10 px, and check the fitted height against the {heightMeters ? `${heightMeters} m` : "height"} you entered — if they disagree, the fit is off rather than the tape measure.</p>
                    </>
                );
        }
    };

    const primary: Record<Step, { text: string; action: () => void; disabled?: boolean }> = {
        key: { text: "Add", action: associate, disabled: !deviceKey || busy },
        offer: { text: "Set it up", action: () => setStep("details"), disabled: busy },
        details: {
            text: "Next",
            action: () => {
                setError(undefined);
                setStep("purpose");
            },
            // Height must be positive, not merely present: Device.Height is a non-nullable float, so
            // leaving it empty serializes the key away and the model binder writes 0 — which reads as
            // a measured value everywhere downstream, including the fit cross-check.
            disabled: busy || !name || !heightMeters || heightMeters <= 0,
        },
        purpose: {
            text: "Next",
            action: async () => {
                setError(undefined);
                if (await saveAnswers()) setStep("photo");
            },
            disabled: busy || mode === undefined,
        },
        photo: {
            // Firmware too old to take a photo on request would otherwise dead-end here, since the
            // only way on was the request button.
            text: photoRequested || !snapshot.canRequestNewPhoto ? "Continue" : "Request snapshot",
            action: photoRequested || !snapshot.canRequestNewPhoto ? () => setStep("done") : requestPhoto,
            disabled: busy || snapshot.isRequesting,
        },
        done: { text: "Open layout", action: finish },
    };

    const current = primary[step];

    return (
        <Dialog
            show={props.show}
            title={titles[step]}
            okButtonText={current.text}
            disableOkButton={current.disabled}
            onOkClick={current.action}
            onCancelClick={props.onCancel}
            footerLeft={step !== "key" && step !== "done" ? <button className="text-sm text-gray-600 hover:text-gray-800" onClick={props.onCancel}>Skip setup</button> : undefined}
        >
            <>{body()}</>
        </Dialog>
    );
};
